import React, { useImperativeHandle, useState } from "react";
import {
  Alert,
  AlertTitle,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { parseBackendNotificationDetails } from "../../utils/notification";

const typeToColor: Record<string, string> = {
  error: "error",
  warning: "warning",
  success: "success",
  info: "info",
};

const typeToIcon: Record<string, string> = {
  error: "error",
  warning: "warning",
  success: "check_circle",
  info: "info",
};

function snackColorToSeverity(
  color: string | undefined,
): "success" | "info" | "warning" | "error" {
  const c = String(color || "info").toLowerCase();
  if (c === "success") return "success";
  if (c === "warning") return "warning";
  if (c === "error" || c === "dark") return "error";
  return "info";
}

function iconNode(iconName: string | undefined, severity: "success" | "info" | "warning" | "error") {
  const n = String(iconName || "").toLowerCase();
  if (n.includes("check") || n === "success") return <CheckCircleIcon fontSize="small" />;
  if (n.includes("warn") || n === "warning" || n === "star") return <WarningIcon fontSize="small" />;
  if (n.includes("error") || n === "error") return <ErrorIcon fontSize="small" />;
  if (n.includes("notif")) return <NotificationsIcon fontSize="small" />;
  if (severity === "success") return <CheckCircleIcon fontSize="small" />;
  if (severity === "warning") return <WarningIcon fontSize="small" />;
  if (severity === "error") return <ErrorIcon fontSize="small" />;
  return <InfoIcon fontSize="small" />;
}

const Notification = React.forwardRef((props, ref) => {
  const normalizeOptions = (opts: any) => {
    const normalized = { ...(opts || {}) };
    const normalizedType = String(normalized.type || "").toLowerCase();
    const backendDetails = parseBackendNotificationDetails(
      normalized.raw || normalized.response || normalized.data,
    );

    if (!normalized.color && normalizedType && typeToColor[normalizedType]) {
      normalized.color = typeToColor[normalizedType];
    }
    if (!normalized.icon && normalizedType && typeToIcon[normalizedType]) {
      normalized.icon = typeToIcon[normalizedType];
    }
    if (!normalized.content && normalized.message) {
      normalized.content = normalized.message;
    }
    if (!normalized.reason && backendDetails.reason) {
      normalized.reason = backendDetails.reason;
    }
    if (!normalized.content && backendDetails.message) {
      normalized.content = backendDetails.message;
    }
    if (!normalized.info && backendDetails.info) {
      normalized.info = backendDetails.info;
    }
    if (!normalized.title && backendDetails.errorCode && backendDetails.reason) {
      normalized.title = `${backendDetails.reason} (${backendDetails.errorCode})`;
    }

    if (!normalized.color && String(normalized.title || "").toLowerCase().includes("error")) {
      normalized.color = "error";
    }
    if (!normalized.icon && normalized.color === "error") {
      normalized.icon = "error";
    }
    if (!normalized.title) {
      normalized.title =
        normalized.color === "success" ? "Operation Successful" : "Notification";
    }

    return normalized;
  };

  useImperativeHandle(ref, () => ({
    show(opts: any) {
      setSnackOptions(normalizeOptions(opts));
      setOpen(true);
    },
  }));

  const [open, setOpen] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    icon: "notifications",
    title: "Title",
    content: "Content",
    reason: "Page Execution",
    color: "info",
    info: "",
  });

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const severity = snackColorToSeverity(snackOptions.color);

  const filledAlertSx = {
    minWidth: { xs: "100%", sm: 360 },
    alignItems: "flex-start",
    color: (theme: Theme) => theme.palette.getContrastText(theme.palette[severity].main),
    "& .MuiAlertTitle-root": {
      color: "inherit",
      fontWeight: 700,
    },
    "& .MuiAlert-message": {
      color: "inherit",
    },
    "& .MuiAlert-message .MuiTypography-root": {
      color: "inherit",
    },
    "& .MuiAlert-icon": {
      color: "inherit",
    },
    "& .MuiAlert-action .MuiIconButton-root": {
      color: "inherit",
    },
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={5000}
      onClose={handleClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      sx={{ mt: 1 }}
    >
      <Alert
        severity={severity}
        variant="filled"
        onClose={handleClose}
        icon={iconNode(snackOptions.icon, severity)}
        sx={filledAlertSx}
      >
        <AlertTitle sx={{ mb: 0.5, color: "inherit" }}>{snackOptions.title || "Notification"}</AlertTitle>
        <Stack spacing={0.5}>
          {snackOptions.reason ? (
            <Typography variant="caption" color="inherit" sx={{ opacity: 0.92 }}>
              {snackOptions.reason}
            </Typography>
          ) : null}
          {snackOptions.content ? (
            <Typography variant="body2" color="inherit">
              {snackOptions.content}
            </Typography>
          ) : null}
          {snackOptions.info ? (
            <Typography variant="caption" color="inherit" sx={{ opacity: 0.92 }}>
              {snackOptions.info}
            </Typography>
          ) : null}
        </Stack>
      </Alert>
    </Snackbar>
  );
});

export default Notification;
